import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Target, Plus, Edit3, Trash2, MessageCircle, ChevronDown, Pencil, X, Check, Flag, Calendar, Dumbbell, Timer, Scale } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Goal } from '@/hooks/useGoals';
import { getGoalProgressPercentage } from '@/utils/goalProgressUtils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '@/hooks/useProfile';
import { usePremium } from '@/hooks/usePremium';
import { usePremiumGate } from '@/contexts/PremiumGateContext';
import { FREE_MAX_ACTIVE_GOALS } from '@/lib/premiumFeatures';
import { useUnits } from '@/hooks/useUnits';
import { SpecificGoalForm, normalizeGoalDraft, type SpecificGoalDraft } from '@/components/SpecificGoalForm';
import { DurationWheelPicker } from '@/components/DurationWheelPicker';
import { daysUntil, formatGoalClock, goalDisplay } from '@/lib/specificGoals';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface GoalProgressCardProps {
  goals: Goal[];
  onCreateGoal: (goal: Omit<Goal, 'id' | 'created_at' | 'updated_at'>) => void;
  onUpdateProgress: (goalId: string, newValue: number) => void;
  onDeleteGoal: (goalId: string) => Promise<void> | void;
}

export const GoalProgressCard = ({ goals, onCreateGoal, onUpdateProgress, onDeleteGoal }: GoalProgressCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isPremium } = usePremium();
  const { openPaywall } = usePremiumGate();
  const units = useUnits();
  const activeGoalCount = goals.filter((g) => g.is_active).length;
  const canAddGoal = isPremium || activeGoalCount < FREE_MAX_ACTIVE_GOALS;
  const defaultWeightUnit = units === 'imperial' ? 'lbs' : 'kg';
  const defaultDistanceUnit = units === 'imperial' ? 'miles' : 'km';
  const { profile, updateProfile } = useProfile();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [showOverallGoalsEditor, setShowOverallGoalsEditor] = useState(false);
  const [editingOverallGoals, setEditingOverallGoals] = useState<string[]>([]);
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);
  const [goalPendingDelete, setGoalPendingDelete] = useState<Goal | null>(null);
  const [deletingGoal, setDeletingGoal] = useState(false);
  const [newProgress, setNewProgress] = useState('');
  const [updateMinutes, setUpdateMinutes] = useState(0);
  const emptyDraft = (): SpecificGoalDraft => ({
    goal_type: 'running',
    exercise_name: '',
    target_value: 0,
    target_unit: defaultDistanceUnit,
    current_value: 0,
    starting_value: null,
    target_date: null,
    is_active: true,
    description: '',
    racePresetId: 'custom',
  });
  const [newGoal, setNewGoal] = useState<SpecificGoalDraft>(emptyDraft());

  useEffect(() => {
    setNewGoal((prev) => ({
      ...prev,
      target_unit:
        prev.goal_type === 'running'
          ? defaultDistanceUnit
          : prev.goal_type === 'endurance'
            ? 'reps'
            : defaultWeightUnit,
    }));
  }, [defaultWeightUnit, defaultDistanceUnit]);

  const primaryGoalOptions = [
    'Build Muscle',
    'Lose Weight',
    'Improve Strength',
    'Increase Endurance',
    'Better Health',
    'Sport Performance',
    'Flexibility',
    'Mental Wellness'
  ];

  const handleOverallGoalToggle = (goal: string) => {
    setEditingOverallGoals(prev => 
      prev.includes(goal) 
        ? prev.filter(g => g !== goal) 
        : [...prev, goal]
    );
  };

  const handleSaveOverallGoals = async () => {
    try {
      await updateProfile({ primary_goals: editingOverallGoals });
      setShowOverallGoalsEditor(false);
      toast.success(t('components.goalCard.toast.overallGoalsUpdated'));
    } catch (error) {
      toast.error(t('components.goalCard.toast.updateGoalsFailed'));
    }
  };

  const openOverallGoalsEditor = () => {
    setEditingOverallGoals(profile?.primary_goals || []);
    setShowOverallGoalsEditor(true);
  };

  const handleAnalyzeGoal = (goal: Goal) => {
    const pct = Math.round(getProgressPercentage(goal));
    const goalMessage = goal.goal_type === 'running'
      ? `Analyze my running race goal "${goal.exercise_name}": ${goal.target_value} ${goal.target_unit}${goal.description ? `, goal time ${goal.description} minutes` : ''}${goal.target_date ? `, race day ${goal.target_date}` : ''}. Progress ${pct}%. Help me get closer.`
      : `Analyze my ${goal.goal_type} goal: ${goal.exercise_name}. Current: ${goal.current_value} ${goal.target_unit}, Target: ${goal.target_value} ${goal.target_unit}. Progress: ${pct}%`;
    
    navigate('/chat', { 
      state: { 
        prefilledMessage: goalMessage,
        goalContext: {
          goal_id: goal.id,
          goal_type: goal.goal_type,
          exercise_name: goal.exercise_name,
          current_value: goal.current_value,
          target_value: goal.target_value,
          target_unit: goal.target_unit,
          progress_percentage: getProgressPercentage(goal)
        }
      } 
    });
  };

  const handleCreateGoal = () => {
    const draft = normalizeGoalDraft(newGoal);
    if (!draft.exercise_name.trim()) {
      toast.error(
        draft.goal_type === 'running'
          ? t('components.goalCard.toast.raceNameRequired', { defaultValue: 'Give your race a name' })
          : t('components.goalCard.toast.exerciseNameRequired'),
      );
      return;
    }
    if (!draft.target_value || draft.target_value <= 0) {
      toast.error(t('components.goalCard.toast.targetValueRequired'));
      return;
    }
    onCreateGoal({
      ...draft,
      target_date: draft.target_date || null,
    });
    setNewGoal(emptyDraft());
    setShowCreateDialog(false);
  };

  const handleUpdateProgress = () => {
    if (!selectedGoal) return;
    const value =
      selectedGoal.goal_type === 'running' ||
      (selectedGoal.goal_type === 'endurance' && selectedGoal.target_unit === 'minutes')
        ? updateMinutes
        : parseFloat(newProgress);
    if (!Number.isFinite(value)) return;
    onUpdateProgress(selectedGoal.id, value);
    setShowUpdateDialog(false);
    setNewProgress('');
    setSelectedGoal(null);
  };

  const confirmDeleteGoal = async () => {
    const id = goalPendingDelete?.id;
    if (!id) return;
    setDeletingGoal(true);
    try {
      await onDeleteGoal(id);
      setShowUpdateDialog(false);
      setSelectedGoal(null);
      setGoalPendingDelete(null);
    } catch {
      // Progress page already toasts the failure; keep the confirm dialog open.
    } finally {
      setDeletingGoal(false);
    }
  };

  const getProgressPercentage = (goal: Goal) => getGoalProgressPercentage(goal);

  const overallGoals = profile?.primary_goals || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-normal">
          <Target className="h-5 w-5 text-primary" />
          My Goals
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overall Goals Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">{t('components.goalCard.overallSection')}</h3>
            {!showOverallGoalsEditor ? (
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={openOverallGoalsEditor}
                className="h-7 text-xs"
              >
                <Pencil className="h-3 w-3 mr-1" />
                Edit
              </Button>
            ) : (
              <div className="flex gap-1">
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => setShowOverallGoalsEditor(false)}
                  className="h-7 text-xs text-muted-foreground"
                >
                  <X className="h-3 w-3" />
                </Button>
                <Button 
                  size="sm" 
                  variant="default" 
                  onClick={handleSaveOverallGoals}
                  className="h-7 text-xs"
                >
                  <Check className="h-3 w-3 mr-1" />
                  Save
                </Button>
              </div>
            )}
          </div>
          
          {showOverallGoalsEditor ? (
            <div className="grid grid-cols-2 gap-2">
              {primaryGoalOptions.map((goal) => (
                <div
                  key={goal}
                  className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${
                    editingOverallGoals.includes(goal)
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => handleOverallGoalToggle(goal)}
                >
                  <Checkbox
                    checked={editingOverallGoals.includes(goal)}
                    onCheckedChange={() => handleOverallGoalToggle(goal)}
                    className="pointer-events-none h-4 w-4"
                  />
                  <span className="text-sm">{goal}</span>
                </div>
              ))}
            </div>
          ) : overallGoals.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {overallGoals.map((goal: string) => (
                <Badge key={goal} variant="secondary" className="text-xs py-1.5 px-3 bg-primary text-black font-semibold">
                  {goal}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t('components.goalCard.emptyOverall')} <button onClick={openOverallGoalsEditor} className="text-primary hover:underline">{t('components.goalCard.addOverall')}</button>
            </p>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-border" />

        {/* Specific Goals Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">{t('components.goalCard.specificSection')}</h3>
            {canAddGoal ? (
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="h-7 text-xs">
                  <Plus className="h-3 w-3 mr-1" />
                  New Goal
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[88vh] overflow-y-auto rounded-2xl border-primary/20 p-5 sm:p-6">
                <DialogHeader>
                  <DialogTitle className="text-left text-lg font-semibold">
                    {t('components.goalCard.createGoalTitle')}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <SpecificGoalForm
                    value={newGoal}
                    onChange={setNewGoal}
                    distanceUnit={defaultDistanceUnit}
                    weightUnit={defaultWeightUnit}
                  />
                  <Button onClick={handleCreateGoal} className="w-full h-11 rounded-xl font-semibold">
                    {t('components.goalCard.createButton', { defaultValue: 'Create Goal' })}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            ) : (
              <button
                type="button"
                onClick={() => openPaywall({ description: t('premium.maxActiveGoals'), source: 'goals_limit' })}
                className="text-[10px] text-primary max-w-[9rem] text-right leading-tight underline underline-offset-2"
              >
                {t('premium.maxActiveGoals', {
                  defaultValue: '1 goal on Free — upgrade for more',
                })}
              </button>
            )}
          </div>
          
          {goals.length === 0 ? (
            <div className="text-center py-6">
              <Target className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground mb-2">{t('components.goalCard.emptySpecific')}</p>
              <p className="text-xs text-muted-foreground">
                {t('components.goalCard.emptySpecificHint', {
                  defaultValue: 'Pick a race, a lift, or a weight target. Arnold will train you toward it.',
                })}
              </p>
            </div>
          ) : (
          <div className="space-y-3">
            {goals.map((goal) => {
              const percentage = getProgressPercentage(goal);
              const daysLeft = daysUntil(goal.target_date);
              const isRun = goal.goal_type === 'running';
              const display = goalDisplay(goal);
              const TypeIcon =
                goal.goal_type === 'running' ? Flag
                : goal.goal_type === 'strength' ? Dumbbell
                : goal.goal_type === 'endurance' ? Timer
                : Scale;

              return (
                <Collapsible key={goal.id} className="rounded-xl bg-card border border-border shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden">
                  <div className="p-4">
                    <CollapsibleTrigger className="w-full">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform duration-200 flex-shrink-0 data-[state=open]:rotate-180" />
                          <div className="flex-1 min-w-0 text-left">
                            <h3 className="text-sm sm:text-base font-semibold text-foreground truncate">{goal.exercise_name}</h3>
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                              <p className="text-xs text-muted-foreground">{display.typeLabel}</p>
                              {isRun && (
                                <span className="text-[10px] font-semibold uppercase tracking-wide text-primary">
                                  {goal.target_value} {goal.target_unit}
                                </span>
                              )}
                              {daysLeft != null && (
                                <span className="text-xs text-foreground/80">
                                  {daysLeft >= 0
                                    ? t('components.goalCard.daysToGo', { count: daysLeft, defaultValue: '{{count}} days to go' })
                                    : t('components.goalCard.daysAgo', { count: Math.abs(daysLeft), defaultValue: '{{count}} days ago' })}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <div className="text-right">
                            <div className="text-lg sm:text-xl font-bold text-primary">{Math.round(percentage)}%</div>
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                              {display.remaining || t('components.goalCard.progressLabel')}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CollapsibleTrigger>

                    <CollapsibleContent className="pt-4">
                      <div className="space-y-4">
                        <div className="rounded-xl bg-gradient-to-br from-primary/15 via-primary/5 to-transparent border border-primary/20 p-3 space-y-3">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                              <TypeIcon className="h-3.5 w-3.5" />
                              {display.currentLabel} → {display.targetLabel}
                            </div>
                            {daysLeft != null && daysLeft >= 0 && (
                              <div className="text-right">
                                <div className="text-2xl font-black text-primary leading-none">{daysLeft}</div>
                                <div className="text-[9px] uppercase tracking-wide text-muted-foreground">
                                  {t('components.goalCard.daysToGoLabel', { defaultValue: 'days to go' })}
                                </div>
                              </div>
                            )}
                          </div>
                          {display.remaining && (
                            <p className="text-sm font-semibold text-foreground">{display.remaining}</p>
                          )}
                          {isRun && percentage === 0 && goal.current_value > 0 && parseFloat(goal.description || '0') > 0 && (
                            <p className="text-xs text-muted-foreground">
                              {t('components.goalCard.progressExplainer', {
                                defaultValue: 'Progress moves when you beat your starting best. Update after a faster run.',
                              })}
                            </p>
                          )}
                          {isRun && (
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <div className="text-[10px] text-muted-foreground uppercase">{t('components.goalCard.distanceLabel', { defaultValue: 'Distance' })}</div>
                                <div className="font-bold">{goal.target_value} {goal.target_unit}</div>
                              </div>
                              <div>
                                <div className="text-[10px] text-muted-foreground uppercase flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {t('components.goalCard.raceDate', { defaultValue: 'Race day' })}
                                </div>
                                <div className="font-bold">
                                  {goal.target_date
                                    ? new Date(`${goal.target_date}T12:00:00`).toLocaleDateString()
                                    : '—'}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedGoal(goal);
                              setNewProgress(goal.current_value.toString());
                              setUpdateMinutes(goal.current_value || 0);
                              setShowUpdateDialog(true);
                            }}
                            className="border-primary/30 hover:border-primary/50 h-8 flex-1"
                          >
                            <Edit3 className="h-3 w-3 mr-2" />
                            {t('components.goalCard.updateProgress', { defaultValue: 'Update' })}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setGoalPendingDelete(goal)}
                            className="h-8 text-destructive border-destructive/30"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>

                        {/* Progress Details */}
                        <div className="flex items-center gap-4 sm:gap-8">
                          {/* Progress Circle */}
                          <div className="relative flex-shrink-0 w-24 h-24 sm:w-32 sm:h-32">
                            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100" style={{ filter: 'drop-shadow(0 0 8px hsl(var(--primary) / 0.5))' }}>
                              <defs>
                                <linearGradient id={`gradient-${goal.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                                  <stop offset="0%" stopColor="hsl(var(--primary))" />
                                  <stop offset="50%" stopColor="hsl(var(--primary) / 0.8)" />
                                  <stop offset="100%" stopColor="hsl(var(--primary) / 0.6)" />
                                </linearGradient>
                              </defs>
                              <circle
                                cx="50"
                                cy="50"
                                r="42"
                                stroke={`url(#gradient-${goal.id})`}
                                strokeWidth="10"
                                fill="transparent"
                                strokeDasharray={`${2 * Math.PI * 42}`}
                                strokeDashoffset={`${2 * Math.PI * 42 * (1 - percentage / 100)}`}
                                className="transition-all duration-1000 ease-out"
                                strokeLinecap="round"
                              />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                              <span className="text-2xl sm:text-3xl font-black text-primary">{Math.round(percentage)}%</span>
                              <span className="text-[9px] sm:text-xs text-muted-foreground font-semibold mt-0.5">{t('components.goalCard.progressLabel')}</span>
                            </div>
                          </div>

                          {/* Stats */}
                          <div className="flex-1 space-y-2 sm:space-y-3">
                            {/* Target Goal */}
                            <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-2 sm:p-3 rounded-lg border border-primary/20">
                              <div className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">{t('components.goalCard.targetGoalLabel')}</div>
                              <div className="text-base sm:text-lg font-bold text-foreground">
                                {goal.goal_type === 'running'
                                  ? formatGoalClock(parseFloat(goal.description || '0'))
                                  : `${goal.target_value} ${goal.target_unit}`
                                }
                              </div>
                            </div>

                            {/* Current Progress */}
                            <div className="bg-muted/30 p-2 sm:p-3 rounded-lg">
                              <div className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">{t('components.goalCard.currentLabel')}</div>
                              <div className="text-base sm:text-lg font-bold text-foreground">
                                {goal.goal_type === 'running'
                                  ? formatGoalClock(goal.current_value)
                                  : `${goal.current_value} ${goal.target_unit}`
                                }
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Analyze Button */}
                        <Button
                          onClick={() => handleAnalyzeGoal(goal)}
                          className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground shadow-md hover:shadow-lg transition-all"
                          size="sm"
                        >
                          <MessageCircle className="h-4 w-4 mr-2" />
                          Analyze with {profile?.ai_coach_name || 'Arnold'}
                        </Button>
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              );
            })}
          </div>
          )}
        </div>

        {/* Update Progress Dialog */}
        <Dialog open={showUpdateDialog} onOpenChange={setShowUpdateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Progress: {selectedGoal?.exercise_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {selectedGoal?.goal_type === 'running' ||
              (selectedGoal?.goal_type === 'endurance' && selectedGoal.target_unit === 'minutes') ? (
                <div>
                  <Label>{t('components.goalCard.currentBestLabel', { defaultValue: 'New time' })}</Label>
                  <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">
                    {t('components.goalCard.scrollTimeHint', { defaultValue: 'Scroll the wheels — no typing.' })}
                  </p>
                  <DurationWheelPicker minutes={updateMinutes} onChange={setUpdateMinutes} />
                </div>
              ) : (
                <div>
                  <Label htmlFor="newProgress">{t('components.goalCard.currentLabel')}</Label>
                  <Input
                    id="newProgress"
                    type="number"
                    step="0.1"
                    value={newProgress}
                    onChange={(e) => setNewProgress(e.target.value)}
                    placeholder={`Current: ${selectedGoal?.current_value}`}
                    className="mt-1.5 h-11 rounded-xl"
                  />
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={handleUpdateProgress} className="flex-1">
                  Update Progress
                </Button>
                <Button 
                  onClick={() => selectedGoal && setGoalPendingDelete(selectedGoal)} 
                  variant="destructive"
                  className="flex-1"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('components.goalCard.deleteGoal', { defaultValue: 'Delete Goal' })}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <AlertDialog
          open={!!goalPendingDelete}
          onOpenChange={(open) => {
            if (!open && !deletingGoal) setGoalPendingDelete(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t('components.goalCard.deleteConfirmTitle', { defaultValue: 'Delete this goal?' })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t('components.goalCard.deleteConfirmBody', {
                  name: goalPendingDelete?.exercise_name || '',
                  defaultValue: '“{{name}}” will be removed. This cannot be undone.',
                })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deletingGoal}>
                {t('components.goalCard.cancel', { defaultValue: 'Cancel' })}
              </AlertDialogCancel>
              <AlertDialogAction
                disabled={deletingGoal}
                onClick={(e) => {
                  e.preventDefault();
                  void confirmDeleteGoal();
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deletingGoal
                  ? t('components.goalCard.deleting', { defaultValue: 'Deleting…' })
                  : t('components.goalCard.deleteGoal', { defaultValue: 'Delete Goal' })}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};
